var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* tslint:disable:no-bitwise no-console */
import { parseDxfMTextContent } from "@dxfom/mtext";
import bspline from "b-spline";
import { DxfParser, } from "dxf-parser";
import { ArcCurve, BufferAttribute, BufferGeometry, DoubleSide, EllipseCurve, Euler, Group, Matrix4, Mesh, Points, Quaternion, Vector2, Vector3, } from "three";
import DxfTypeface from "three/examples/fonts/droid/droid_sans_regular.typeface.json";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils";
import { PickingLineMaterial, PickingMeshBasicMaterial, PickingMeshStandardMaterial, PickingPointsMaterial, } from "../../materials";
import { MathUtils } from "../../math";
import { FileLoader } from "./FileLoader";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry";
/**
 * DXF model file loader implementation
 */
export class DxfLoader {
    static load(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const content = yield FileLoader.loadText(url, { cacheKey: options.cacheKey, cache: true });
            const dxf = DxfLoader.DXF_PARSER.parseSync(content);
            if (dxf === null || !dxf.entities) {
                throw new Error(`Could not parse DXF file ${url}`);
            }
            const materialGroups = new Map();
            for (const entity of dxf.entities) {
                const object = DxfLoader.drawEntity(entity, dxf);
                if (Array.isArray(object)) {
                    object.forEach((subobject) => DxfLoader.addToMaterialGroup(subobject, materialGroups));
                }
                else if (object !== undefined) {
                    DxfLoader.addToMaterialGroup(object, materialGroups);
                }
            }
            const group = new Group();
            group.add(DxfLoader.createMesh(materialGroups, options));
            return group;
        });
    }
    /**
     * Register a Dxf object to the material groups map.
     *
     * @param object
     * @param materialGroups
     */
    static addToMaterialGroup(object, materialGroups) {
        const key = JSON.stringify(object.material);
        const group = materialGroups.get(key);
        if (group === undefined) {
            materialGroups.set(key, [object]);
        }
        else {
            group.push(object);
        }
    }
    /**
     * Merge geometries in Vector3 space before converting to BufferGeometry to preserve numerical precision.
     *
     * @param geometries
     * @param isLine
     * @returns
     */
    static mergeObjects(objects, isLine) {
        // Combine vertices
        const vertices = [];
        for (const object of objects) {
            const coords = object.geometry.getAttribute("position").array;
            for (let i = 0; i < coords.length; i += 3) {
                const vertex = new Vector3(coords[i] + object.translation.x, coords[i + 1] + object.translation.y, coords[i + 2] + object.translation.z);
                vertices.push(vertex);
            }
        }
        // Normalize points
        const translation = computeCentroid(vertices);
        for (const vertex of vertices) {
            vertex.sub(translation);
        }
        // Create new geometry
        const positions = new Float32Array(vertices.length * 3);
        for (let i = 0; i < vertices.length; i++) {
            positions[i * 3 + 0] = vertices[i].x;
            positions[i * 3 + 1] = vertices[i].y;
            positions[i * 3 + 2] = vertices[i].z;
        }
        let geometry;
        if (isLine) {
            geometry = new LineSegmentsGeometry().setPositions(positions);
        }
        else {
            geometry = new BufferGeometry();
            geometry.setAttribute("position", new BufferAttribute(positions, 3));
        }
        return { geometry, translation };
    }
    /**
     * Construct the final mesh from the existing material groups for meshes, points, lines, and text.
     *
     * @param materialGroups
     * @param options
     * @returns
     */
    static createMesh(materialGroups, options) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const group = new Group();
        // Create geometries and materials
        for (const [key, objects] of materialGroups) {
            const materialDesc = JSON.parse(key);
            const { geometry, translation } = DxfLoader.mergeObjects(objects, materialDesc.type === "line");
            objects.forEach((object) => object.geometry.dispose());
            // Skip empty geometries
            if (geometry.attributes.position.count === 0) {
                geometry.dispose();
                continue;
            }
            let object3d;
            if (materialDesc.type === "mesh") {
                geometry.computeVertexNormals();
                geometry.computeBoundsTree();
                const material = new PickingMeshStandardMaterial({
                    objectId: (_a = options.objectId) !== null && _a !== void 0 ? _a : 0,
                    clippingPlanes: (_b = options.clippingPlanes) !== null && _b !== void 0 ? _b : [],
                    color: materialDesc.color,
                    side: DoubleSide,
                    forceSinglePass: true,
                });
                object3d = new Mesh(geometry, material);
            }
            else if (materialDesc.type === "point") {
                const material = new PickingPointsMaterial({
                    objectId: (_c = options.objectId) !== null && _c !== void 0 ? _c : 0,
                    clippingPlanes: (_d = options.clippingPlanes) !== null && _d !== void 0 ? _d : [],
                    color: materialDesc.color,
                    size: materialDesc.size,
                    sizeAttenuation: false,
                    forceSinglePass: true,
                });
                object3d = new Points(geometry, material);
            }
            else if (materialDesc.type === "line") {
                const material = new PickingLineMaterial({
                    objectId: (_e = options.objectId) !== null && _e !== void 0 ? _e : 0,
                    clippingPlanes: (_f = options.clippingPlanes) !== null && _f !== void 0 ? _f : [],
                    color: materialDesc.color,
                    linewidth: materialDesc.width,
                    gapSize: materialDesc.solid ? undefined : 0.1,
                    dashSize: materialDesc.solid ? undefined : 0.2,
                    worldUnits: materialDesc.width !== 1,
                    forceSinglePass: true,
                });
                material.defines.USE_DASH = !materialDesc.solid;
                object3d = new LineSegments2(geometry, material);
                // @ts-ignore
                object3d.computeLineDistances();
            }
            else {
                geometry.computeVertexNormals();
                geometry.computeBoundsTree();
                const material = new PickingMeshBasicMaterial({
                    objectId: (_g = options.objectId) !== null && _g !== void 0 ? _g : 0,
                    clippingPlanes: (_h = options.clippingPlanes) !== null && _h !== void 0 ? _h : [],
                    color: materialDesc.color,
                    side: DoubleSide,
                    forceSinglePass: true,
                });
                object3d = new Mesh(geometry, material);
            }
            object3d.position.copy(translation);
            group.add(object3d);
        }
        return group;
    }
    /**
     * Draw each entity.
     *
     * @param entity
     * @param dxf
     */
    static drawEntity(entity, dxf) {
        if (entity.type === "POINT") {
            return DxfLoader.drawPoint(entity, dxf);
        }
        else if (entity.type === "ARC") {
            return DxfLoader.drawArc(entity, dxf);
        }
        else if (entity.type === "CIRCLE") {
            return DxfLoader.drawArc(entity, dxf);
        }
        else if (entity.type === "ELLIPSE") {
            return DxfLoader.drawEllipse(entity, dxf);
        }
        else if (entity.type === "LINE") {
            return DxfLoader.drawLine(entity, dxf);
        }
        else if (entity.type === "POLYLINE") {
            return DxfLoader.drawPolyline(entity, dxf);
        }
        else if (entity.type === "LWPOLYLINE") {
            return DxfLoader.drawPolyline(entity, dxf);
        }
        else if (entity.type === "SPLINE") {
            return DxfLoader.drawSpline(entity, dxf);
        }
        else if (entity.type === "SOLID") {
            return DxfLoader.drawSolid(entity, dxf);
        }
        else if (entity.type === "3DFACE") {
            return DxfLoader.draw3DFace(entity, dxf);
        }
        else if (entity.type === "INSERT") {
            return DxfLoader.drawBlock(entity, dxf);
        }
        else if (entity.type === "TEXT") {
            return DxfLoader.drawText(entity, dxf);
        }
        else if (entity.type === "MTEXT") {
            return DxfLoader.drawMText(entity, dxf);
            // } else if (entity.type === "ATTDEF") {
            //     return DxfLoader.drawText(entity as IAttdefEntity, dxf);
        }
        else if (entity.type === "DIMENSION") {
            const dimEntity = entity;
            const dimTypeEnum = dimEntity.dimensionType & 7;
            if (dimTypeEnum === 0) {
                return DxfLoader.drawDimension(dimEntity, dxf);
            }
            else {
                console.warn(`DxfLoader - Unsupported Dimension Type: ${dimTypeEnum}`);
            }
        }
        else {
            console.warn(`DxfLoader - Unsupported Entity Type: ${entity.type}`);
        }
        return;
    }
    /**
     * Get the color of an entity.
     *
     * @param entity
     * @param dxf
     */
    static getColor(entity, dxf) {
        var _a, _b, _c, _d, _e, _f;
        let color = 0xffffff;
        if (entity.color !== undefined) {
            color = entity.color;
        }
        else if (((_c = (_b = (_a = dxf.tables) === null || _a === void 0 ? void 0 : _a.layer) === null || _b === void 0 ? void 0 : _b.layers) === null || _c === void 0 ? void 0 : _c[entity === null || entity === void 0 ? void 0 : entity.layer]) !== undefined) {
            color = (_f = (_e = (_d = dxf.tables) === null || _d === void 0 ? void 0 : _d.layer) === null || _e === void 0 ? void 0 : _e.layers) === null || _f === void 0 ? void 0 : _f[entity === null || entity === void 0 ? void 0 : entity.layer].color;
        }
        if (color == null) {
            color = 0xffffff;
        }
        return color;
    }
    /**
     * Create a BufferGeometry from a curve.
     *
     * @param curve
     * @param samples
     * @returns
     */
    static lineGeometryFromPoints(points) {
        const vertices = [];
        for (let i = 0; i < points.length - 1; i++) {
            const curr = points[i];
            const next = points[i + 1];
            vertices.push(curr, next);
        }
        return new BufferGeometry().setFromPoints(vertices);
    }
    static drawPoint(entity, dxf) {
        const geometry = new BufferGeometry().setFromPoints([new Vector3()]);
        const translation = new Vector3(entity.position.x, entity.position.y, entity.position.z);
        const color = DxfLoader.getColor(entity, dxf);
        return {
            geometry,
            translation,
            material: {
                type: "point",
                color,
                size: 3,
            },
        };
    }
    static drawArc(entity, dxf) {
        let curve;
        if (entity.type === "CIRCLE") {
            curve = new ArcCurve(0, 0, entity.radius);
        }
        else {
            curve = new ArcCurve(0, 0, entity.radius, entity.startAngle, entity.endAngle);
        }
        const geometry = DxfLoader.lineGeometryFromPoints(curve.getPoints(32));
        const translation = new Vector3(entity.center.x, entity.center.y, entity.center.z);
        const color = DxfLoader.getColor(entity, dxf);
        return {
            geometry,
            translation,
            material: {
                type: "line",
                color,
                width: 1,
                solid: true,
            },
        };
    }
    static drawEllipse(entity, dxf) {
        const xrad = Math.sqrt(Math.pow(entity.majorAxisEndPoint.x, 2) + Math.pow(entity.majorAxisEndPoint.y, 2));
        const yrad = xrad * entity.axisRatio;
        const rotation = Math.atan2(entity.majorAxisEndPoint.y, entity.majorAxisEndPoint.x);
        const curve = new EllipseCurve(0, 0, xrad, yrad, entity.startAngle, entity.endAngle, false, // Always counterclockwise
        rotation);
        const geometry = DxfLoader.lineGeometryFromPoints(curve.getPoints(50));
        const translation = new Vector3(entity.center.x, entity.center.y, entity.center.z);
        const color = DxfLoader.getColor(entity, dxf);
        return {
            geometry,
            translation,
            material: {
                type: "line",
                color,
                width: 1,
                solid: true,
            },
        };
    }
    static drawLine(entity, dxf) {
        var _a, _b, _c;
        if (!entity.vertices)
            throw new Error("Entity missing vertices.");
        const points = entity.vertices.map((point) => new Vector3(point.x, point.y, point.z));
        const translation = computeCentroid(points);
        const adjusted = points.map((point) => point.clone().sub(translation));
        const geometry = DxfLoader.lineGeometryFromPoints(adjusted);
        const color = DxfLoader.getColor(entity, dxf);
        const lineType = (_c = (_b = (_a = dxf.tables) === null || _a === void 0 ? void 0 : _a.lineType) === null || _b === void 0 ? void 0 : _b.lineTypes) === null || _c === void 0 ? void 0 : _c[entity === null || entity === void 0 ? void 0 : entity.lineType];
        const dotted = lineType && lineType.pattern && lineType.pattern.length !== 0;
        return {
            geometry,
            translation,
            material: {
                type: "line",
                color,
                width: 1,
                solid: !dotted,
            },
        };
    }
    static drawPolyline(entity, dxf) {
        var _a, _b, _c;
        if (!entity.vertices)
            throw new Error("Entity missing vertices.");
        // Compute all points on the path, including the bulge points
        const points = [];
        const n = entity.vertices.length;
        for (let i = 0; i < n; i++) {
            const curr = entity.vertices[i];
            const next = entity.vertices[(i + 1) % n];
            if (curr.bulge) {
                points.push(...getBulgeCurvePoints(curr, next, curr.bulge));
            }
            else {
                points.push(new Vector3(curr.x, curr.y, curr.z));
            }
        }
        if (entity.shape) {
            points.push(points[0]);
        }
        const translation = computeCentroid(points);
        const adjusted = points.map((point) => point.clone().sub(translation));
        const geometry = DxfLoader.lineGeometryFromPoints(adjusted);
        const color = DxfLoader.getColor(entity, dxf);
        const lineType = (_c = (_b = (_a = dxf.tables) === null || _a === void 0 ? void 0 : _a.lineType) === null || _b === void 0 ? void 0 : _b.lineTypes) === null || _c === void 0 ? void 0 : _c[entity === null || entity === void 0 ? void 0 : entity.lineType];
        const dotted = ((lineType === null || lineType === void 0 ? void 0 : lineType.pattern) && lineType.pattern.length !== 0) || !entity.hasContinuousLinetypePattern;
        const width = entity.thickness || entity.width || 1;
        return {
            geometry,
            translation,
            material: {
                type: "line",
                color,
                width,
                solid: !dotted,
            },
        };
    }
    static drawSpline(entity, dxf) {
        const vertices = getBSplinePolyline(entity.controlPoints, entity.degreeOfSplineCurve, entity.knotValues, 100);
        const points = vertices.map((point) => new Vector3(point.x, point.y));
        const translation = computeCentroid(points);
        const adjusted = points.map((point) => point.clone().sub(translation));
        const geometry = DxfLoader.lineGeometryFromPoints(adjusted);
        const color = DxfLoader.getColor(entity, dxf);
        return {
            geometry,
            translation,
            material: {
                type: "line",
                color,
                width: 1,
                solid: true,
            },
        };
    }
    static drawSolid(entity, dxf) {
        const points = entity.points.map((point) => new Vector3(point.x, point.y, point.z));
        const vertices = [];
        triangulatePoints(points[0], points[1], points[2], vertices);
        triangulatePoints(points[1], points[2], points[3], vertices);
        const translation = computeCentroid(points);
        const adjusted = vertices.map((point) => point.clone().sub(translation));
        const geometry = new BufferGeometry().setFromPoints(adjusted);
        const color = DxfLoader.getColor(entity, dxf);
        return {
            geometry,
            translation,
            material: {
                type: "mesh",
                color,
            },
        };
    }
    static draw3DFace(entity, dxf) {
        const points = entity.vertices.map((point) => new Vector3(point.x, point.y, point.z));
        const vertices = [];
        if (points.length > 4) {
            vertices.push(points[0], points[1], points[2]);
            vertices.push(points[0], points[2], points[3]);
        }
        else {
            vertices.push(points[0], points[1], points[2]);
        }
        const translation = computeCentroid(points);
        const adjusted = vertices.map((point) => point.clone().sub(translation));
        const geometry = new BufferGeometry().setFromPoints(adjusted);
        const color = DxfLoader.getColor(entity, dxf);
        return {
            geometry,
            translation,
            material: {
                type: "mesh",
                color,
            },
        };
    }
    static drawText(entity, dxf) {
        if (entity.type === "ATTDEF" && entity.text === "X") {
            console.log(entity);
        }
        const geometry = new TextGeometry(parseSpecialChars(entity.text), {
            font: DxfLoader.DXF_FONT,
            size: entity.textHeight,
            height: 0,
        });
        // Orient and translate
        if (entity.rotation)
            geometry.rotateZ(MathUtils.degreesToRadians(entity.rotation));
        const translation = new Vector3(entity.startPoint.x, entity.startPoint.y, entity.startPoint.z);
        const color = DxfLoader.getColor(entity, dxf);
        return {
            geometry,
            translation,
            material: {
                type: "text",
                color,
            },
        };
    }
    static drawMText(entity, dxf) {
        const parsed = parseDxfMTextContent(parseSpecialChars(entity.text));
        const styling = getMTextStyling(parsed, entity);
        const lines = styling.text.split("\n");
        const lineGeometries = [];
        const lineHeight = (entity.height * 5) / 3;
        const remHeight = lineHeight - entity.height;
        // Compute line geometries and block size
        let blockX = 0;
        let blockY = 0;
        for (const line of lines) {
            const textGeometry = new TextGeometry(line, {
                font: DxfLoader.DXF_FONT,
                size: entity.height,
                height: 0,
            });
            lineGeometries.push(textGeometry);
            // Update block size
            textGeometry.computeBoundingBox();
            const lineX = textGeometry.boundingBox.max.x - textGeometry.boundingBox.min.x;
            const lineY = textGeometry.boundingBox.max.y - textGeometry.boundingBox.min.y;
            blockX = Math.max(lineX, blockX);
            blockY = Math.max(lineY, blockY);
        }
        // Apply per-line styling and apply line offset
        let offsetX;
        let offsetY;
        for (let i = 0; i < lineGeometries.length; i++) {
            const lineGeometry = lineGeometries[i];
            const lineWidth = lineGeometry.boundingBox.max.x - lineGeometry.boundingBox.min.x;
            const remWidth = blockX - lineWidth;
            offsetX = styling.alignX * (remWidth * 0.5);
            offsetY = styling.alignY * (remHeight * 0.5) + i * lineHeight;
            lineGeometry.translate(offsetX, -offsetY, 0);
        }
        // Generate block geometry and apply block offset
        const geometry = mergeGeometries(lineGeometries);
        const attachX = (entity.attachmentPoint - 1) % 3;
        const attachY = entity.attachmentPoint / 3 - 1;
        offsetX = -attachX * (blockX * 0.5);
        offsetY = -attachY * (blockY * 0.5);
        geometry.translate(offsetX, offsetY, 0);
        // Orient and position in world space
        if (entity.rotation)
            geometry.rotateZ(MathUtils.degreesToRadians(entity.rotation));
        if (entity.directionVector) {
            const dv = new Vector3(entity.directionVector.x, entity.directionVector.y, entity.directionVector.z);
            geometry.rotateZ(dv.angleTo(MathUtils.X_AXIS));
        }
        const translation = new Vector3(entity.position.x, entity.position.y, entity.position.z);
        const color = DxfLoader.getColor(entity, dxf);
        return {
            geometry,
            translation,
            material: {
                type: "text",
                color,
            },
        };
    }
    // TODO: Not sure if this is correct, find a test case
    static drawBlock(entity, dxf) {
        const block = dxf.blocks[entity.name];
        if (!block.entities)
            return;
        const euler = new Euler();
        if (entity.rotation)
            euler.z = MathUtils.degreesToRadians(entity.rotation);
        const position = new Vector3(entity.position.x, entity.position.y, entity.position.z);
        const scale = new Vector3(entity.xScale, entity.yScale, 1);
        const rotation = new Quaternion().setFromEuler(euler);
        // Compute transformation matrix
        const transform = new Matrix4().compose(position, rotation, scale);
        // Create child entities
        const children = [];
        for (const childEntity of block.entities) {
            const child = DxfLoader.drawEntity(childEntity, dxf);
            if (Array.isArray(child)) {
                child.forEach((subobject) => subobject.geometry.applyMatrix4(transform));
                children.push(...child);
            }
            else if (child !== undefined) {
                child.geometry.applyMatrix4(transform);
                children.push(child);
            }
        }
        return children;
    }
    static drawDimension(entity, dxf) {
        const block = dxf.blocks[entity.block];
        if (!block || !block.entities)
            return;
        const children = [];
        for (const childEntity of block.entities) {
            const child = DxfLoader.drawEntity(childEntity, dxf);
            if (Array.isArray(child)) {
                children.push(...child);
            }
            else if (child !== undefined) {
                children.push(child);
            }
        }
        return children;
    }
}
DxfLoader.DXF_PARSER = new DxfParser();
DxfLoader.DXF_FONT = new FontLoader().parse(DxfTypeface);
/**
 * Compute the centroid of a set of points.
 *
 * @param points
 * @returns
 */
function computeCentroid(points) {
    const centroid = new Vector3();
    for (const point of points) {
        centroid.add(point);
    }
    centroid.divideScalar(points.length);
    return centroid;
}
/**
 * Calculates points for a curve between two points using a bulge value. Typically used in polylines.
 *
 * @param start
 * @param end
 * @param bulge
 */
function getBulgeCurvePoints(start, end, bulge) {
    const a = new Vector2(start.x, start.y);
    const b = new Vector2(end.x, end.y);
    const angle = 4 * Math.atan(bulge);
    const radius = a.distanceTo(b) / 2 / Math.sin(angle / 2);
    const center = MathUtils.polar(a, radius, MathUtils.angle2(a, b) + (Math.PI / 2 - angle / 2));
    const segments = Math.max(Math.abs(Math.ceil(angle / (Math.PI / 18))), 6); // By default want a segment roughly every 10 degrees
    const startAngle = MathUtils.angle2(center, a);
    const thetaAngle = angle / segments;
    const vertices = [];
    vertices.push(new Vector3(a.x, a.y, 0));
    for (let i = 1; i <= segments - 1; i++) {
        const vertex = MathUtils.polar(center, Math.abs(radius), startAngle + thetaAngle * i);
        vertices.push(new Vector3(vertex.x, vertex.y, 0));
    }
    return vertices;
}
/**
 * Order triangle vertices to produce visually correct geometry.
 *
 * @param a
 * @param b
 * @param c
 * @param vertices
 */
function triangulatePoints(a, b, c, vertices = []) {
    // Calculate which direction the points are facing (clockwise or counter-clockwise)
    const ab = new Vector3().subVectors(b, a);
    const ac = new Vector3().subVectors(c, a);
    ab.cross(ac);
    // If z < 0 then we must draw these in reverse order
    if (ab.z < 0) {
        vertices.push(c, b, a);
    }
    else {
        vertices.push(a, b, c);
    }
    return vertices;
}
/**
 * Compute spline polyline coordinates.
 *
 * @param controlPoints
 * @param degree
 * @param knots
 * @param lerpsPerSegment
 * @returns
 */
function getBSplinePolyline(controlPoints, degree, knots, lerpsPerSegment) {
    const bsplinePoints = controlPoints.map((point) => [point.x, point.y]);
    const segmentTs = [knots[degree]];
    const domain = [knots[degree], knots[knots.length - 1 - degree]];
    for (let k = degree + 1; k < knots.length - degree; ++k) {
        if (segmentTs[segmentTs.length - 1] !== knots[k]) {
            segmentTs.push(knots[k]);
        }
    }
    const polyline = [];
    for (let i = 1; i < segmentTs.length; ++i) {
        const uMin = segmentTs[i - 1];
        const uMax = segmentTs[i];
        for (let k = 0; k <= lerpsPerSegment; ++k) {
            const u = (k / lerpsPerSegment) * (uMax - uMin) + uMin;
            // Clamp t to 0, 1 to handle numerical precision issues
            const t = Math.max(0, Math.min(1, (u - domain[0]) / (domain[1] - domain[0])));
            try {
                const p = bspline(t, degree, bsplinePoints, knots);
                polyline.push(new Vector2(p[0], p[1]));
            }
            catch (error) {
                console.warn(`BSpline - ${error.message}`);
            }
        }
    }
    return polyline;
}
/**
 * Get MText styling
 *
 * @param parsed
 * @param entity
 * @param color
 * @returns
 */
function getMTextStyling(parsed, entity) {
    let alignX = 0; // 0=left, 1=center, 2=right
    let alignY = 2; // 0=top, 1=middle, 2=bottom
    const textLines = [];
    for (const item of parsed) {
        if (typeof item === "string") {
            if (item.startsWith("pxq") && item.endsWith(";")) {
                if (item.includes("l"))
                    alignX = 0;
                else if (item.includes("c"))
                    alignX = 1;
                else if (item.includes("r"))
                    alignX = 2;
                else if (item.includes("j"))
                    alignX = 0;
                else if (item.includes("d"))
                    alignX = 0;
            }
            else {
                textLines.push(item);
            }
        }
        else if (Array.isArray(item)) {
            const nestedFormat = getMTextStyling(item, entity);
            textLines.push(nestedFormat.text);
        }
        else if (typeof item === "object") {
            const { S, A } = item;
            if (S !== undefined) {
                textLines.push(S[0] + "/" + S[2]);
            }
            if (A !== undefined) {
                alignY = A;
            }
        }
    }
    return { text: textLines.join(), alignX, alignY };
}
/**
 * Parse special characters in text entities and convert to unicode.
 *
 * @param text
 * @returns
 */
const SPECIAL_CHARS_RE = /(?:%%([dpcou%]))|(?:\\U\+([0-9a-fA-F]{4}))/g;
function parseSpecialChars(text) {
    return text.replaceAll(SPECIAL_CHARS_RE, (match, p1, p2) => {
        if (p1 !== undefined) {
            switch (p1) {
                case "d":
                    return "\xb0";
                case "p":
                    return "\xb1";
                case "c":
                    return "\u2205";
                case "o":
                    /* Toggles overscore mode on and off, not implemented. */
                    return "";
                case "u":
                    /* Toggles underscore mode on and off, not implemented. */
                    return "";
                case "%":
                    return "%";
            }
        }
        else if (p2 !== undefined) {
            const code = parseInt(p2, 16);
            if (isNaN(code)) {
                return match;
            }
            return String.fromCharCode(code);
        }
        return match;
    });
}
